<template>
  <header class="site-header" ref="headerRoot">
    <div class="container site-header__inner">
      <router-link :to="SITE_HOME" class="site-header__logo">
        <img
          title="hireclap-logo"
          src="/hireclap-logo-small.webp"
          alt="Hireclap | hire from video profiles"
        >
      </router-link>

      <nav v-if="!user" class="site-header__nav" aria-label="Main navigation">
        <router-link :to="JOBS_LIST_PATH" class="site-header__link site-header__link--accent">
          Jobs
        </router-link>
        <div
          class="site-header__dropdown"
          :class="{ 'site-header__dropdown--open': learnMenuOpen }"
        >
          <button
            type="button"
            class="site-header__link site-header__dropdown-trigger"
            :aria-expanded="learnMenuOpen ? 'true' : 'false'"
            aria-haspopup="true"
            @click.stop="toggleLearnMenu"
          >
            Learn
            <img
              class="site-header__dropdown-caret"
              :src="$S3_url + '/Assets/images/caret-down-solid.svg'"
              alt=""
              width="10"
              height="10"
            >
          </button>
          <div
            v-show="learnMenuOpen"
            class="site-header__dropdown-menu"
            role="menu"
          >
            <button
              type="button"
              class="site-header__dropdown-item"
              role="menuitem"
              @click="goToChallenges"
            >Challenges</button>
            <button
              type="button"
              class="site-header__dropdown-item"
              role="menuitem"
              @click="goToCourses"
            >Courses</button>
          </div>
        </div>
        <a href="/recruit" target="_blank" rel="noopener" class="site-header__link">For Employers</a>
        <a href="/recruit/post-job" target="_blank" rel="noopener" class="site-header__link">Post free job</a>
      </nav>

      <div class="site-header__actions">
        <template v-if="user">
          <button type="button" class="site-header__search" @click="getJobList">
            Search job
          </button>
          <div class="site-header__user" @click="toggleUserMenu">
            <div
              v-if="!user.visume_path"
              class="site-header__avatar site-header__avatar--add"
              @click.stop="openModal('visume')"
            >+</div>
            <div v-else class="site-header__avatar site-header__avatar--has-video" @click.stop="getProfile">
              <video
                :key="headerVisumeSrc"
                :src="headerVisumeSrc"
                preload="metadata"
                muted
                playsinline
                webkit-playsinline
                @loadedmetadata="primeVisumeFrame"
                @loadeddata="primeVisumeFrame"
              />
            </div>
            <div class="site-header__user-name" v-if="user.name">
              {{ displayName }}
              <span v-if="!user.visume_path" class="site-header__user-hint">Add visume</span>
            </div>
            <img
              class="site-header__caret"
              :src="$S3_url + '/Assets/images/caret-down-solid.svg'"
              alt=""
              width="10"
              height="10"
            >
            <div v-if="showUserMenu" class="site-header__menu">
              <button type="button" class="site-header__menu-item" @click="getProfile">My profile</button>
              <button type="button" class="site-header__menu-item" @click="showConfirmation('logout_confirmation')">
                Logout
              </button>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="site-header__ctas">
            <router-link
              :to="{ name: 'JobseekerSign', query: authRouteQuery }"
              class="hp-btn hp-btn--outline"
            >Login</router-link>
            <router-link
              :to="{ name: 'SeekerReg', query: authRouteQuery }"
              class="hp-btn hp-btn--primary"
            >Register</router-link>
          </div>
        </template>

        <button
          v-if="!user"
          type="button"
          class="site-header__toggle"
          :aria-expanded="mobileOpen ? 'true' : 'false'"
          aria-label="Toggle navigation"
          @click="toggleMobile"
        >
          <span class="site-header__toggle-icon"></span>
        </button>
      </div>
    </div>

    <div v-if="!user" class="site-header__mobile" :class="{ 'site-header__mobile--open': mobileOpen }">
      <nav class="site-header__mobile-nav" aria-label="Mobile navigation">
        <router-link
          :to="JOBS_LIST_PATH"
          class="site-header__mobile-link site-header__mobile-link--accent"
          @click.native="closeMobile"
        >
          <span>Jobs</span>
          <span class="site-header__mobile-chevron" aria-hidden="true">›</span>
        </router-link>
        <button type="button" class="site-header__mobile-link" @click="goToChallenges">
          <span>Challenges</span>
          <span class="site-header__mobile-chevron" aria-hidden="true">›</span>
        </button>
        <button type="button" class="site-header__mobile-link" @click="goToCourses">
          <span>Courses</span>
          <span class="site-header__mobile-chevron" aria-hidden="true">›</span>
        </button>
        <a href="/recruit" target="_blank" rel="noopener" class="site-header__mobile-link">
          <span>For Employers</span>
          <span class="site-header__mobile-chevron" aria-hidden="true">›</span>
        </a>
        <a href="/recruit/post-job" target="_blank" rel="noopener" class="site-header__mobile-link">
          <span>Post free job</span>
          <span class="site-header__mobile-chevron" aria-hidden="true">›</span>
        </a>
      </nav>
      <div class="site-header__mobile-ctas">
        <router-link
          :to="{ name: 'JobseekerSign', query: authRouteQuery }"
          class="hp-btn hp-btn--outline"
          @click.native="closeMobile"
        >Login</router-link>
        <router-link
          :to="{ name: 'SeekerReg', query: authRouteQuery }"
          class="hp-btn hp-btn--primary"
          @click.native="closeMobile"
        >Register</router-link>
      </div>
    </div>

    <Modals :open="modals.open" />

    <b-modal
      ref="upload-resume"
      title="Upload Resume"
      content-class="hc-modal"
      header-class="hc-modal__header"
      body-class="hc-modal__body"
      hide-footer
    >
      <v-form ref="form" lazy-validation v-model="valid">
        <v-file-input
          v-model="resume"
          accept=".doc,.docx,.rtf,.pdf"
          placeholder="Post resume"
          :append-icon="$icons.file"
          prepend-icon=""
          :rules="[v => !!v || 'Upload valid resume']"
          show-size
          outlined
        ></v-file-input>
      </v-form>
      <div class="hc-modal__actions">
        <button type="button" class="hp-btn hp-btn--outline" @click.prevent="closeModal('upload-resume')">Cancel</button>
        <button type="button" class="hp-btn hp-btn--primary" @click.prevent="uploadResume()" v-if="!loading">Save</button>
        <button type="button" class="hp-btn hp-btn--primary" disabled v-if="loading">Saving…</button>
      </div>
    </b-modal>

    <b-modal
      ref="logout_confirmation"
      content-class="hc-modal"
      body-class="hc-modal__body hc-modal__body--confirm"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
    >
      <div class="hc-confirm">
        <div class="hc-confirm__icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </div>
        <h2 class="hc-confirm__title">Log out?</h2>
        <p class="hc-confirm__text">Are you sure you want to log out of your Hireclap account?</p>
        <div class="hc-confirm__actions">
          <button type="button" class="hp-btn hp-btn--outline" @click.prevent="logout()" v-if="!loading">Log out</button>
          <button type="button" class="hp-btn hp-btn--primary" @click.prevent="cancelConfirmation('logout_confirmation')" v-if="!loading">Stay signed in</button>
        </div>
        <p v-if="loading" class="hc-confirm__loading">Logging out…</p>
      </div>
    </b-modal>
  </header>
</template>

<script>
const Modals = () => import(/* webpackPrefetch: true */ '@/components/Modals');
import { eventBus } from '@/main';
import AwsUpload from '../../../utils/s3upload';
import Actions from '@/libraries/apiActions.js';
import { SITE_HOME, JOBS_LIST_PATH } from '@/utils/previewRoutes';
import { buildAuthRouteQuery } from '@/utils/authRouteQuery';
import { scrollToSection } from '@/utils/scrollToSection';

import '@/assets/home-page.css';
import '@/assets/site-chrome.css';

export default {
  name: 'SiteHeader',
  components: { Modals },
  data() {
    return {
      SITE_HOME,
      JOBS_LIST_PATH,
      action: new Actions(),
      user: null,
      modals: { open: 'exit' },
      visumeModal: { open: 'exit' },
      showUserMenu: false,
      learnMenuOpen: false,
      mobileOpen: false,
      resume: null,
      update: { resume: null },
      valid: true,
      loading: false,
    };
  },
  computed: {
    displayName() {
      if (!this.user || !this.user.name) return '';
      return this.user.name.length > 12 ? `${this.user.name.substring(0, 12)}…` : this.user.name;
    },
    authRouteQuery() {
      return buildAuthRouteQuery(this.$route);
    },
    headerVisumeSrc() {
      if (!this.user || !this.user.visume_path) return '';
      const clean = String(this.user.visume_path).split('#')[0];
      return `${this.$S3_bucket_url}${clean}`;
    },
  },
  watch: {
    $route() {
      this.syncUserFromCookie();
      this.mobileOpen = false;
      this.showUserMenu = false;
      this.learnMenuOpen = false;
    },
  },
  beforeMount() {
    this.syncUserFromCookie();
    if (this.$cookies.get('seeker')) {
      this.$cookies.remove('seeker');
    }
  },
  created() {
    eventBus.$on('seeker-updated', this.syncUserFromCookie);
  },
  mounted() {
    this.syncUserFromCookie();
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    eventBus.$off('seeker-updated', this.syncUserFromCookie);
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    syncUserFromCookie() {
      this.user = this.$cookies.get('hire-seeker') || null;
    },
    primeVisumeFrame(event) {
      const video = event && event.target;
      if (!video) return;
      try {
        if (video.readyState >= 1) {
          video.currentTime = 0.1;
        }
      } catch {
        /* iOS may reject seek before metadata */
      }
    },
    openModal(type = '') {
      if (type === 'visume' && this.user) {
        eventBus.$emit('open', 'visume', this.user.visume_path, 0, this.visumeModal.open);
      }
      this.modals.open = 'exit';
      this.modals.open = type;
    },
    showConfirmation(type) {
      this.showUserMenu = false;
      this.$refs[type].show();
    },
    cancelConfirmation(type) {
      this.$refs[type].hide();
    },
    closeModal(ref) {
      this.$refs[ref].hide();
    },
    handleDocumentClick(e) {
      if (this.$refs.headerRoot && !this.$refs.headerRoot.contains(e.target)) {
        this.showUserMenu = false;
        this.learnMenuOpen = false;
        this.mobileOpen = false;
      }
    },
    toggleLearnMenu() {
      this.learnMenuOpen = !this.learnMenuOpen;
      this.showUserMenu = false;
      this.mobileOpen = false;
    },
    closeLearnMenu() {
      this.learnMenuOpen = false;
    },
    scrollToAnchor(anchorId) {
      scrollToSection(anchorId);
    },
    scrollToUpskillingCourses() {
      scrollToSection('upskilling-courses', { behavior: 'smooth', settle: true });
    },
    goToChallenges() {
      this.closeMobile();
      const onPosts = this.$route.name === 'posts';
      if (onPosts) {
        this.closeLearnMenu();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      this.$router.push({ name: 'posts' }).then(() => {
        this.closeLearnMenu();
        window.scrollTo({ top: 0, behavior: 'auto' });
      }).catch(() => {});
    },
    goToCourses() {
      this.closeLearnMenu();
      this.closeMobile();
      const onHome = this.$route.name === 'home'
        || this.$route.path === SITE_HOME
        || this.$route.path === '/';
      if (onHome) {
        if (this.$route.hash !== '#upskilling-courses') {
          this.$router.replace({ hash: '#upskilling-courses' }).catch(() => {});
        }
        this.$nextTick(() => this.scrollToUpskillingCourses());
        return;
      }
      window.scrollTo(0, 0);
      this.$router.push({ name: 'home', hash: '#upskilling-courses' }).catch(() => {});
    },
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu;
      this.learnMenuOpen = false;
      this.mobileOpen = false;
    },
    toggleMobile() {
      this.mobileOpen = !this.mobileOpen;
      this.showUserMenu = false;
      this.learnMenuOpen = false;
    },
    closeMobile() {
      this.mobileOpen = false;
    },
    getJobList(e) {
      if (e && e.preventDefault) e.preventDefault();
      if (this.$route.path !== JOBS_LIST_PATH) {
        this.$router.push(JOBS_LIST_PATH);
      }
    },
    getProfile() {
      if (this.$route.path !== '/myprofile') {
        this.$router.push('/myprofile');
      }
      this.showUserMenu = false;
    },
    async uploadResume() {
      const token = this.$cookies.get('hire-seeker').token;
      this.valid = this.$refs.form.validate();
      if (this.valid !== true) return;

      this.loading = true;
      const awsUploader = new AwsUpload();
      try {
        const extension = this.resume.name.split('.').pop();
        const d = new Date();
        const month = d.getMonth() + 1;
        const day = d.getDate();
        const currentDate = `${d.getFullYear()}/${month < 10 ? '0' : ''}${month}/${day < 10 ? '0' : ''}${day}`;
        const timestamp = Math.floor(Date.now() / 1000);
        const filename = `resume/${currentDate}/${this.user.name}${timestamp}.${extension}`;
        const response = await awsUploader.uploadToS3(this.resume, filename);
        this.update.resume = response.url;
        this.action.updateResume(this.update.resume, token).then(data => {
          if (data.message === 'Resume Updated') {
            this.loading = false;
            this.resume = false;
            this.$refs['upload-resume'].hide();
            const cookie = this.$cookies.get('hire-seeker');
            cookie.alert = cookie.visume_path ? 'no' : 'yes';
            const updatedCookie = {
              token: cookie.token,
              name: cookie.name,
              visume_path: cookie.visume_path,
              resume_path: this.update.resume,
              alert: cookie.alert,
            };
            this.$cookies.remove('hire-seeker');
            this.$cookies.set('hire-seeker', updatedCookie);
          } else {
            this.loading = false;
          }
        }).catch(err => {
          this.loading = false;
          console.log(err);
        });
      } catch (error) {
        this.loading = false;
        console.error('Failed to upload file to S3:', error);
      }
    },
    logout() {
      this.loading = true;
      const data = this.$cookies.get('hire-seeker');
      this.action.seekerLogout(data.token).then(res => {
        this.loading = false;
        if (res.status === '1') {
          this.$cookies.remove('hire-seeker');
          this.showUserMenu = false;
          this.user = null;
          this.$refs.logout_confirmation.hide();
          if (this.$route.path !== SITE_HOME) {
            this.$router.push(SITE_HOME);
          }
        }
      }).catch(err => {
        this.loading = false;
        console.log(err);
        location.reload();
      });
    },
  },
};
</script>
